import React from 'react';

import { tv } from '../../tv';

/**
 * Lays children out in one direction with a consistent gap.
 *
 * This is the component that lets spacing stop being a margin. A margin on a
 * child describes a relationship between siblings, which is the parent's
 * business: it is why the same gap ends up written on the first element in
 * one file and the last element in another, and why removing an item can
 * leave the spacing wrong.
 *
 * Gap is a closed step from the spacing scale, so a layout cannot invent
 * seventeen pixels of space.
 */

const stackStyles = tv({
  base: 'ck:flex',
  variants: {
    direction: {
      column: 'ck:flex-col',
      row: 'ck:flex-row',
    },
    gap: {
      0: 'ck:gap-0',
      1: 'ck:gap-1',
      2: 'ck:gap-2',
      3: 'ck:gap-3',
      4: 'ck:gap-4',
      6: 'ck:gap-6',
      8: 'ck:gap-8',
      12: 'ck:gap-12',
    },
    align: {
      start: 'ck:items-start',
      center: 'ck:items-center',
      end: 'ck:items-end',
      stretch: 'ck:items-stretch',
    },
    justify: {
      start: 'ck:justify-start',
      center: 'ck:justify-center',
      end: 'ck:justify-end',
      between: 'ck:justify-between',
    },
    wrap: {
      true: 'ck:flex-wrap',
      false: '',
    },
    /* Fills the space its parent allocated, instead of hugging its content.
     * A filled card gives its content slot the leftover height, but a stack
     * inside that slot still hugs, so nothing reaches the bottom edge and a
     * growing child has nothing to grow into. Atlassian ships the identical
     * control on its Stack as `grow: 'hug' | 'fill'`; the name here follows
     * Card's `isFilled`, because both mean fill what you were given.
     * The min sizes match ScrollArea's: a flex child's minimum defaults to
     * its content, so without them a parent that runs out of room cannot
     * size the stack below its content, and a scrolling child inside it
     * never gets to scroll. */
    filled: {
      true: 'ck:size-full ck:min-h-0 ck:min-w-0',
      false: '',
    },
    /* A list stack is a list for assistive technology, not for the eye: the
     * browser's bullets and indent are the layout's to drop. */
    list: {
      true: 'ck:m-0 ck:list-none ck:p-0',
      false: '',
    },
  },
  defaultVariants: { direction: 'column', gap: 4, wrap: false, filled: false, list: false },
});

export interface StackProps {
  children?: React.ReactNode;
  /** Stacks vertically by default, which is the common case. */
  direction?: 'column' | 'row';
  /** Space between children, as a step on the spacing scale. */
  gap?: 0 | 1 | 2 | 3 | 4 | 6 | 8 | 12;
  /** Alignment across the stack's direction. */
  align?: 'start' | 'center' | 'end' | 'stretch';
  /** Distribution along the stack's direction. */
  justify?: 'start' | 'center' | 'end' | 'between';
  /** Wraps onto more lines when the children do not fit. */
  isWrap?: boolean;
  /**
   * Fills the space the parent allocated, rather than hugging the content, and lets a flex
   * parent size the stack below its content so a scrolling child inside it can scroll.
   */
  isFilled?: boolean;
  /**
   * The element to render. Use `ul` or `ol` where the children are a list: it is a
   * list for assistive technology, and the browser's bullets and indent are dropped.
   */
  as?: 'div' | 'section' | 'ul' | 'ol' | 'li';
  id?: string;
}

export const Stack = React.forwardRef<HTMLElement, StackProps>(function Stack(
  {
    children,
    direction = 'column',
    gap = 4,
    align,
    justify,
    isWrap = false,
    isFilled = false,
    as: As = 'div',
    ...props
  },
  ref,
) {
  const isList = As === 'ul' || As === 'ol';
  return (
    <As
      {...props}
      // A polymorphic element has no single ref type across div/ul/li, so the
      // cast is confined here rather than pushed onto callers.
      ref={ref as React.Ref<never>}
      className={stackStyles({
        direction,
        gap,
        align,
        justify,
        wrap: isWrap,
        filled: isFilled,
        list: isList,
      })}
      // WebKit stops exposing an unstyled list as a list without the explicit role.
      role={isList ? 'list' : undefined}
      data-component="stack"
    >
      {children}
    </As>
  );
});
